import React, { useEffect, useRef, useMemo, useState, useContext, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { FormattedMessage } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values */
import { Stack, xcss, Box } from '@atlaskit/primitives';

import { CONTENT_STATUS_ARCHIVED } from '@confluence/archive-pages/entry-points/archivingConstants';
import { useAnnotationsIsOffline } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext';
import { PAGE_COMMENTS_METRIC } from '@confluence/comments-section/entry-points/pageSegmentLoadMetrics';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder';
import { ReactionsProvider } from '@confluence/comment-context/entry-points/ReactionsContext';
import {
	useObjectCommentContext,
	useObjectCommentDispatchContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext';
import { ObjectCommentQueryParamsListener } from '@confluence/comments-query-param-handlers/entry-points/ObjectCommentQueryParamsListener';
import type { useActiveCommentsQueryVariables } from '@confluence/comments-data/entry-points/useActiveCommentsQuery';
import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery';
import {
	useAllCommentsData,
	buildDefaultCommentsContentStatusFilters,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';
import {
	CommentType,
	FooterCommentsLoadingState,
	type CommentsDataMap as LegacyCommentsDataMap,
	useCommentsDataState,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { CommentsType } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';
import {
	getAnnotationThreadKeys,
	getOpenCommentThreads,
	LocationValue,
	onlyHasReactionsSummaryErrors,
} from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import { getVisibleCommentThreads } from '@confluence/comments-panel-utils/entry-points/getVisibleCommentThreads';
import {
	AddFooterCommentMode,
	useCommentsPanelState,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import {
	getCommentPositionTopOfSectionEditor,
	getCommentPositionTopOfSectionRenderer,
	getWidthAndMaxWidth,
} from '@confluence/comments-util/entry-points/domUtils';
import {
	getTranslatedError,
	isNetworkError,
	isViewNotPermittedError,
} from '@confluence/comments-util/entry-points/objectCommentsErrorUtils';
import { objectCommentsi18n } from '@confluence/comments-util/entry-points/objectCommentsi18n';
import { useContentState } from '@confluence/content-state/entry-points/useContentState';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { VIEW_PAGE_COMMENTS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { usePageContentId } from '@confluence/page-context';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { usePageRecommendationData } from '@confluence/page-recommendations/entry-points/usePageRecommendationData';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import { useUnreadComments } from '@confluence/unread-comments/entry-points/useUnreadComments';

import { usePageContentDimensions } from '../hooks/usePageContentDimensions';
import { hideWhiteSpace } from '../utils/hideWhiteSpace';

import { ObjectAddCommentContainer } from './ObjectAddCommentContainer';
import { ObjectCommentsList } from './ObjectCommentsList';
import { ObjectCommentHeader } from './ObjectCommentHeader';
import { ObjectAddCommentEditor } from './ObjectAddCommentEditor';
import { ObjectAddCommentProfileWrapper } from './ObjectAddCommentProfileWrapper';

const commentPanelContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: '100%',
	width: '100%',
});

const wrapperStyle = xcss({
	position: 'relative',
	visibility: 'visible',
	minHeight: '50px',
	margin: '0 auto',
});

const baseEditorStyles = xcss({
	zIndex: 'modal',
	backgroundColor: 'elevation.surface',
	borderTopWidth: 'border.width',
	borderTopStyle: 'solid',
	borderColor: 'color.border',
	padding: 'space.200',
	position: 'relative',
});

const footerStickyWrapperStyle = xcss({
	zIndex: 'navigation',
	backgroundColor: 'elevation.surface',
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	bottom: 'space.0',
});

const viewPageFooterStyle = xcss({
	zIndex: '1',
	transform: 'unset',
	bottom: 'space.0',
});

const footerEditorStyle = xcss({
	left: 'space.0',
});

const fixedEditorStyles = xcss({
	position: 'fixed',
});

const stickyEditorStyles = xcss({
	position: 'sticky',
});

const placeholderStyle = xcss({
	width: '60%',
});

const incrementalLoadingPlaceholderStyle = xcss({
	width: '60%',
});

const addCommentContainerStyles = xcss({
	transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
	transformOrigin: 'top',
	position: 'sticky',
	bottom: 'space.0',
	zIndex: '1', // Required for create comment editor overflow to show on top of page content
});

const addCommentContainerAnimationHiddenStyles = xcss({
	transform: 'translateY(100%)',
	opacity: 0,
	pointerEvents: 'none',
});

const addCommentContainerAnimationShowStyles = xcss({
	transform: 'translateY(0)',
	opacity: 1,
	pointerEvents: 'auto',
});

type ObjectCommentContainerProps = {
	pageWidthType?: string;
	showLabels: boolean;
	hasReactions: boolean;
};

const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

const ObjectCommentContainerComponent = withFlags(
	({
		flags,
		pageWidthType,
		showLabels,
		hasReactions,
	}: WithFlagsProps & ObjectCommentContainerProps) => {
		const [contentId] = usePageContentId();
		const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
		const wrapperRef = useRef<HTMLDivElement>(null);
		const [commentSectionTop, setCommentSectionTop] = useState<number>(0);
		const [isInViewport, setIsInViewport] = useState(false);
		const [allCommentsRendered, setAllCommentsRendered] = useState(false);
		const [commentBatchRendered, setCommentBatchRendered] = useState(false);
		const [commentBatchCount, setCommentBatchCount] = useState(0);
		const [forceLoadComments, setForceLoadComments] = useState(false);
		const [shouldShowAddComment, setShouldShowAddComment] = useState(true);
		const [state] = useContentState();
		const pageMode = useGetPageMode();
		const isServer = useRenderServerPlaceholder();
		const { contentStatus } = useContentStatus();
		const { expanded, width, footerEditorEntry } = useObjectCommentContext();
		const { closeCommentContainer } = useObjectCommentDispatchContext();

		// Track page-content-only dimensions for layout alignment and blue background sizing
		const {
			width: pageContentWidth,
			left: pageContentLeft,
			right: pageContentRight,
		} = usePageContentDimensions();

		const {
			orderedActiveAnnotationIdList,
			commentsDataMap: legacyCommentsDataMap,
			removedThreadsMap,
			footerCommentsLoadingState,
		} = useCommentsDataState();

		// New comments data manager hooks - only used when experiment is enabled
		const newCommentsDataResult = useAllCommentsData({
			contentId: contentId || '',
			contentStatus: buildDefaultCommentsContentStatusFilters(pageMode),
			options: {
				skip: !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) || !contentId,
			},
		});

		const [shouldRequeryArchivedComments, setShouldRequeryArchivedComments] = useState(false);
		const hasRequeriedArchivedComments = useRef(false);
		const startedExperience = useRef(false);

		const focusedCommentId = useFocusedCommentIdQueryParam();
		const replyToComment = useReplyToCommentQueryParam();

		const {
			showInlineComments,
			showGeneralComments,
			replyToCommentId,
			commentType,
			currentlySelectedThreadKey,
			location: commentLocation,
		} = useCommentsPanelState();

		const [{ isUnreadQueryLoading }] = useUnreadComments();
		const [hasLoadedUnreadStatus, setHasLoadedUnreadStatus] = useState(false);
		const experienceTracker = useContext(ExperienceTrackerContext);

		const isContentReady = pageMode === PageMode.VIEW ? state.isContentReady : true;

		// Check if the page is archived
		const isArchived = contentStatus === CONTENT_STATUS_ARCHIVED;

		// Hide add comment container on archived pages
		useEffect(() => {
			if (isArchived) {
				setShouldShowAddComment(false);
				setShouldRequeryArchivedComments(true);
			}
		}, [isArchived]);

		// Close comment container when expanded and contentId changes
		useEffect(() => {
			if (!fg('confluence_comment_sticky_footer_enabled')) {
				return;
			}
			if (expanded && contentId) {
				closeCommentContainer();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [contentId]);

		// AKA - has the user scrolled to the bottom of the page?
		const isContentInViewport = isInViewport && isContentReady;

		// Query setup - skip if not forcing load and not in viewport and not loaded yet
		const commentsQueryHook = useActiveCommentsQuery;

		// When preloading, we don't always have access to archived page state (depends on a race condition for cache hit / miss)
		// In those cases, we can incorrectly determine that the page is fully loaded with no comments, when really we just queried using the wrong vars
		// Given that archived are such a small percent of loads, just force a reload here (i.e. don't trust cache) to be safe
		const shouldForceReloadForArchivedComments =
			shouldRequeryArchivedComments && !hasRequeriedArchivedComments.current;

		const wasFullyLoaded = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
			? newCommentsDataResult.isDataProcessed
			: footerCommentsLoadingState === FooterCommentsLoadingState.FULLY_LOADED &&
				!shouldForceReloadForArchivedComments;

		const wasPartiallyPreloaded =
			footerCommentsLoadingState === FooterCommentsLoadingState.PARTIALLY_LOADED;

		const isPreloading = footerCommentsLoadingState === FooterCommentsLoadingState.PRELOADING;

		const shouldLoadFromNetwork =
			!wasFullyLoaded && !isPreloading && (isContentInViewport || forceLoadComments);

		const commentsQueryHookVariables: useActiveCommentsQueryVariables = {
			pageId: contentId || '',
			skip: wasFullyLoaded || expVal('cc_comments_new_comments_data_manager', 'isEnabled', false), // Skip once we have all data - essentially, we don't need to subscribe to apollo updates after this point since we get everything from sweet state
			type: CommentsType.FOOTER,
			returnNativeLoading: true, // required for `loading` to behave as expected
			useStableCommentsDataUpdates: true,
			fetchPolicy: shouldLoadFromNetwork ? 'network-only' : 'cache-only',
			isPreload: isServer,
		};

		const { hasRecs } = usePageRecommendationData();

		const legacyQueryResult = commentsQueryHook(commentsQueryHookVariables);

		// Use new or legacy data based on experiment
		const {
			loading: baseLoading,
			error,
			data,
		} = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
			? {
					...newCommentsDataResult,
					data: {
						comments: {
							totalCount: newCommentsDataResult.commentCount,
							nodes: newCommentsDataResult.openFooterComments,
						},
					},
					error: newCommentsDataResult.error,
				}
			: legacyQueryResult;

		// Enhanced loading state that accounts for new data manager processing
		// When using new data manager, only show loading if we're actually loading OR if data isn't processed yet
		const loading = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
			? baseLoading || !newCommentsDataResult.isDataProcessed
			: baseLoading;

		// In cases where the first paint will be correct (i.e. will match SPA version because nothing controlled by a useEffect / DOM action), immediately render
		// Otherwise, this will just happen on first paint in SPA once the useEffects run
		const renderOnServer = isServer && data?.comments?.totalCount === 0;

		const noOpenObjectComments = useMemo(() => {
			return data?.comments?.nodes?.length === 0 || true;
		}, [data]);

		// If we're forcing a reload for archived comments, we need to set the flag to true so we don't try to reload again
		useEffect(() => {
			if (shouldForceReloadForArchivedComments && !loading && data) {
				hasRequeriedArchivedComments.current = true;
			}
		}, [data, loading, shouldForceReloadForArchivedComments]);

		// AKA - are we done loading?
		const shouldShowComments = wasFullyLoaded || wasPartiallyPreloaded || renderOnServer;

		// Return null when we haven't loaded anything yet, we're not in the viewport, and we're not SSRed
		const shouldReturnNull =
			(error &&
				(!onlyHasReactionsSummaryErrors(error) ||
					!fg('confluence_frontend_exp_ignore_reaction_errors'))) ||
			(!isContentInViewport && !shouldShowComments);

		// Show global placeholder (i.e. instead of comments header, etc) when we're in the viewport but are still loading, and we're not SSRed
		const shouldShowGlobalPlaceholder = isContentInViewport && !shouldShowComments;

		// Show placeholder when we're in the viewport but are still loading the rest of the data
		const shouldShowIncrementalLoadingPlaceholder = shouldShowComments && loading;

		// Set up IntersectionObserver
		useEffect(() => {
			if (isServer || !isContentReady) {
				// Don't register observer if page content is not ready
				return;
			}

			// eslint-disable-next-line no-restricted-syntax
			const observer = new IntersectionObserver(
				([entry]) => {
					const isIntersecting = entry.isIntersecting;
					if (isIntersecting) {
						setIsInViewport(isIntersecting);
					}
				},
				{
					root: null,
					rootMargin: '10px',
					threshold: 0.1,
				},
			);

			if (wrapperRef.current) {
				observer.observe(wrapperRef.current);
			}

			return () => {
				observer.disconnect();
			};
		}, [isServer, pageMode, state.isContentReady, isContentReady]);

		// Add MutationObserver to detect when comments are actually rendered --- old logic for checking all comment rendered or not
		useEffect(() => {
			if (fg('confluence_frontend_page_comments_race_condition')) {
				return;
			}
			if (isServer || !currentlySelectedThreadKey) {
				return;
			}

			const observer = new MutationObserver(() => {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const commentElement = document.getElementById(commentElementId);

				if (commentElement) {
					setAllCommentsRendered(true);
					observer.disconnect(); // Disconnect once we find the element
				} else {
					setAllCommentsRendered(false); // Reset if element not found
				}
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true,
			});

			// Reset allCommentsRendered when unmounting or changing thread key
			return () => {
				setAllCommentsRendered(false);
				observer.disconnect();
			};
		}, [currentlySelectedThreadKey, isServer]);

		// Update loaded state when data is received
		useEffect(() => {
			if (wasFullyLoaded) {
				setForceLoadComments(false);
			}
		}, [wasFullyLoaded]);

		useEffect(() => {
			if (!isUnreadQueryLoading && !hasLoadedUnreadStatus) {
				setHasLoadedUnreadStatus(true);
			}
		}, [isUnreadQueryLoading, hasLoadedUnreadStatus]);

		// Handle scrolling after comments are loaded
		useEffect(() => {
			if (fg('confluence_comments_unified_navigation')) {
				return;
			}
			if (shouldShowComments && commentType === CommentType.GENERAL && allCommentsRendered) {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const isCommentExists = document.getElementById(commentElementId);
				if (isCommentExists) {
					const rect = isCommentExists.getBoundingClientRect();
					const commentIsInViewport =
						rect.top >= 0 &&
						rect.left >= 0 &&
						rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
						rect.right <= (window.innerWidth || document.documentElement.clientWidth);
					if (!commentIsInViewport) {
						isCommentExists.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
							inline: 'center',
						});
					}
				}
			}
		}, [
			wasFullyLoaded,
			commentType,
			currentlySelectedThreadKey,
			allCommentsRendered,
			hasLoadedUnreadStatus,
			shouldShowComments,
		]);

		useEffect(() => {
			if (commentType === CommentType.GENERAL) {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const isCommentExists = document.getElementById(commentElementId);
				if (!isCommentExists) {
					setForceLoadComments(true);
				}
			}
		}, [currentlySelectedThreadKey, commentType]);

		// Handle spacing for Editor & LiveDoc modes by observing the size of editorTextArea
		useEffect(() => {
			const editorTextArea = document.getElementById('ak-editor-textarea');
			if (editorTextArea) {
				if (
					// eslint-disable-next-line confluence-feature-gating/no-preconditioning
					hasRecs &&
					expValEquals('confluence_ai_related_content_for_live_pages', 'isEnabled', true) &&
					fg('confluence_frontend_unify_footer_comment_space_ld')
				) {
					setCommentSectionTop(0);
					return;
				}

				const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
					if (fg('confluence_footer_comment_styling_change')) {
						const newTop = getCommentPositionTopOfSectionEditor(entries);
						flushSync(() => {
							newTop !== undefined && setCommentSectionTop(newTop);
						});
					}
				});
				// observe resize of editorTextArea to update spacing when content changes
				resizeObserver.observe(editorTextArea);
				// observe resize of document.body to update spacing when window height changes
				resizeObserver.observe(document.body);
				return () => resizeObserver.disconnect();
			}
		}, [hasRecs]);

		// Handle spacing for Renderer mode based on content and window height
		useEffect(() => {
			const renderContentContainer = document.querySelector(
				'[data-testid="content-container-component"]',
			);
			if (
				pageMode === PageMode.VIEW &&
				renderContentContainer &&
				fg('confluence_frontend_unify_footer_comment_space_old')
			) {
				const resizeObserver = new ResizeObserver((_entries: ResizeObserverEntry[]) => {
					const newTop = getCommentPositionTopOfSectionRenderer();
					flushSync(() => {
						setCommentSectionTop(newTop);
					});
				});
				// observe resize of content-container-component to update spacing when content changes
				// (the container includes page title, content, and end-of-page recommendations components, etc.)
				resizeObserver.observe(renderContentContainer);
				// observe resize of document.body to update spacing when window height changes
				resizeObserver.observe(document.body);
				return () => {
					resizeObserver.disconnect();
				};
			}
		}, [pageMode]);

		// Create commentsDataMap from new data when experiment is enabled
		const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
			? (newCommentsDataResult.commentsDataMap as LegacyCommentsDataMap)
			: legacyCommentsDataMap;

		const commentThreads = useMemo(() => {
			if (!wasFullyLoaded && !shouldShowComments) {
				// no need to update if we haven't loaded, or aren't showing comments
				return [];
			}

			return fg('confluence_frontend_content_wrapper')
				? getVisibleCommentThreads({
						commentsDataMap,
						includeGeneralComments: true,
						includeInlineComments: false,
						orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
					})
				: getOpenCommentThreads({
						commentsDataMap,
						showInlineComments,
						showGeneralComments,
						location: LocationValue.FOOTER,
						orderedActiveAnnotationIdList,
						removedThreadsMap,
					});
		}, [
			wasFullyLoaded,
			shouldShowComments,
			commentsDataMap,
			orderedActiveAnnotationIdList,
			showInlineComments,
			showGeneralComments,
			removedThreadsMap,
		]);

		const isOffline = useAnnotationsIsOffline();

		const hasComments = commentThreads.length > 0;

		// Handle add comment container animation and mounting
		useEffect(() => {
			if (isArchived) {
				return;
			}
			const shouldHide = !!replyToCommentId && commentLocation === LocationValue.FOOTER;

			if (shouldHide) {
				// Start hiding animation
				setShouldShowAddComment(false);
			} else {
				// Show component after a small delay to allow for animation
				const timer = setTimeout(() => {
					setShouldShowAddComment(true);
				}, 50);
				return () => clearTimeout(timer);
			}
		}, [replyToCommentId, commentLocation, isArchived]);

		// Track the start of comments loading and initialize performance metrics
		useEffect(() => {
			let shouldStartExperience = false;
			if (fg('confluence_fe_view_page_comments_exp_coverage')) {
				shouldStartExperience =
					!startedExperience.current &&
					((isContentInViewport && loading) || (wasFullyLoaded && hasComments));
			} else {
				// Only start tracking on incremental loading - otherwise all data was preloaded
				// If confluence_frontend_always_ssr_footer_comments is off, we need to use global placeholder as incremental one will never show
				shouldStartExperience =
					shouldShowIncrementalLoadingPlaceholder ||
					(shouldShowGlobalPlaceholder && !fg('confluence_frontend_always_ssr_footer_comments'));
			}
			if (shouldStartExperience) {
				// Start tracking the page comments experience with initial attributes
				experienceTracker.start({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					attributes: {
						isConditionalRenderingOn: true,
						isFocusedComment: Boolean(focusedCommentId || replyToComment),
						contentId,
						pageMode,
					},
				});
				startedExperience.current = true;

				// Initialize and mark the start of page comments rendering metrics
				PAGE_COMMENTS_METRIC.start();
				PAGE_COMMENTS_METRIC.mark('pageCommentsRenderStart');
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			shouldShowIncrementalLoadingPlaceholder,
			shouldShowGlobalPlaceholder,
			loading,
			isContentInViewport,
			wasFullyLoaded,
			hasComments,
		]);

		// Track any errors that occur during comments loading
		useEffect(() => {
			if (error) {
				if (
					onlyHasReactionsSummaryErrors(error) &&
					fg('confluence_frontend_exp_ignore_reaction_errors')
				) {
					experienceTracker.updateAttributes({
						name: VIEW_PAGE_COMMENTS_EXPERIENCE,
						attributes: {
							ignoredReactionsSummaryErrors: true,
						},
					});
					// Comments may still be displayed without reactions, so do not
					// fail or abort the experience but just return here.
					return;
				}

				const translatedError = getTranslatedError(error);

				if (
					isViewNotPermittedError(translatedError) &&
					fg('confluence_frontend_view_page_comments_known_errs')
				) {
					// If the error is a 403 permission error, show a warning flag and abort the experience
					void flags.showWarningFlag({
						title: <FormattedMessage {...objectCommentsi18n.unableToLoadCommentsTitle} />,
						description: <FormattedMessage {...translatedError} />,
					});

					experienceTracker.abort({
						name: VIEW_PAGE_COMMENTS_EXPERIENCE,
						reason: objectCommentsi18n.viewNotPermitted.id,
					});
					PAGE_COMMENTS_METRIC.cancel();
					return;
				}

				if (
					isNetworkError(translatedError) &&
					fg('confluence_frontend_view_page_comments_known_errs')
				) {
					// Show flag for network error
					void flags.showWarningFlag({
						title: <FormattedMessage {...objectCommentsi18n.unableToLoadCommentsTitle} />,
						description: <FormattedMessage {...translatedError} />,
					});
					// The experience will be aborted by experienceTracker.stopOnError() below.
				}

				if (fg('confluence_frontend_view_page_comments_known_errs')) {
					// Mark the experience as failed/aborted and include the error details
					experienceTracker.stopOnError({
						name: VIEW_PAGE_COMMENTS_EXPERIENCE,
						error,
					});
				} else {
					// Mark the experience as failed and include the error details
					experienceTracker.fail({
						name: VIEW_PAGE_COMMENTS_EXPERIENCE,
						error,
					});
				}
				PAGE_COMMENTS_METRIC.cancel();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [error]);

		// Track successful completion of comments rendering
		useEffect(() => {
			if (
				allCommentsRendered &&
				wasFullyLoaded &&
				(hasComments || !fg('confluence_fe_view_page_comments_exp_coverage'))
			) {
				// Mark the experience as successful and include total comments count
				experienceTracker.succeed({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					attributes: {
						totalComments: data?.comments?.totalCount,
						totalCommentThreads: commentThreads.length,
					},
				});

				// Mark key performance metrics:
				// - pageCommentsRenderEnd: End of comments rendering
				PAGE_COMMENTS_METRIC.mark('pageCommentsRenderEnd');
				// Stop the metric tracking and include final data
				PAGE_COMMENTS_METRIC.stop({
					customData: {
						contentId: contentId || '',
						totalComments: data?.comments?.totalCount || 0,
					},
				});
			} else if (
				!allCommentsRendered &&
				shouldShowComments &&
				!shouldReturnNull &&
				!focusedCommentId &&
				!replyToComment &&
				data?.comments?.totalCount !== 0 &&
				fg('confluence_frontend_page_comments_race_condition')
			) {
				// Mark the experience as failed when wrapper is not visible but should be
				experienceTracker.fail({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					error: new Error('Object comment wrapper not visible on screen'),
				});
				PAGE_COMMENTS_METRIC.cancel();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [allCommentsRendered, shouldShowComments, focusedCommentId, replyToComment, wasFullyLoaded]);

		// Track successful completion of first comment batch rendering
		useEffect(() => {
			if (commentBatchRendered) {
				// Mark key performance metrics:
				// - fmp: First Meaningful Paint
				// - tti: Time to Interactive
				PAGE_COMMENTS_METRIC.markFMP();
				PAGE_COMMENTS_METRIC.mark('tti');

				const experienceState = experienceTracker.getExperienceState(VIEW_PAGE_COMMENTS_EXPERIENCE);

				if (experienceState?.startTime && typeof experienceState.startTime === 'number') {
					experienceTracker.updateAttributes({
						name: VIEW_PAGE_COMMENTS_EXPERIENCE,
						attributes: {
							firstMeaningfulPaint: window.performance.now() - experienceState.startTime,
						},
					});
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [commentBatchRendered]);

		// Handle case when there are no comments to render
		useEffect(() => {
			if (data?.comments?.totalCount === 0) {
				// Abort the experience tracking since there are no comments to render
				experienceTracker.abort({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					reason: 'No page comments',
				});
				PAGE_COMMENTS_METRIC.cancel();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [data]);

		const handleAllCommentsRendered = useCallback(() => setAllCommentsRendered(true), []);

		const computedWidths = getWidthAndMaxWidth({
			pageMode,
			pageWidthType,
			hasEndOfPageRecommendations: hasRecs ?? false,
		});

		const shouldRenderFixedStyle = useMemo(() => {
			if (fg('confluence_comment_sticky_footer_enabled')) {
				if (expanded && footerEditorEntry === AddFooterCommentMode.STICKY_DRAFT) {
					return true;
				}
			}
			return false;
		}, [expanded, footerEditorEntry]);

		const pageContentDimensions = useMemo(
			() => ({
				width: pageContentWidth,
				left: pageContentLeft,
				right: pageContentRight,
			}),
			[pageContentWidth, pageContentLeft, pageContentRight],
		);

		return (
			<>
				<Box
					ref={wrapperRef}
					xcss={wrapperStyle}
					testId="object-comment-wrapper"
					style={{
						top: commentSectionTop,
						width: computedWidths.width,
						maxWidth: computedWidths.maxWidth,
						// display: 'contents', // So wrapper doesn't affect layout if empty
					}}
					data-vc={`object-comment-wrapper-${pageMode}-${hasComments ? 'has-comments' : 'no-comments'}`}
				>
					<>
						<ObjectCommentQueryParamsListener
							hasLoadedObjectComments={!!shouldShowComments}
							allObjectCommentsRendered={
								allCommentsRendered || noOpenObjectComments || wasFullyLoaded
							}
							commentBatchRendered={commentBatchRendered}
							commentBatchCount={commentBatchCount}
							setForceLoadComments={setForceLoadComments}
						/>
						{shouldReturnNull ? null : (
							<>
								{shouldShowGlobalPlaceholder && (
									<Box xcss={placeholderStyle}>
										<CommentPlaceholder showPlaceholder />
									</Box>
								)}
								{shouldShowComments && (
									<ReactionsProvider contentId={contentId ?? ''}>
										{hasComments && (
											<ObjectCommentHeader
												isOffline={isOffline}
												showReactions={isEmbeddedConfluence ? hasReactions : true}
											/>
										)}
										<Stack xcss={commentPanelContainerStyles}>
											<ObjectCommentsList
												commentThreads={commentThreads}
												supportedTopLevelActions={['edit', 'resolve', 'delete']}
												flags={flags}
												onAllCommentsRendered={handleAllCommentsRendered}
												onCommentBatchRendered={() => {
													setCommentBatchRendered(true);
													setCommentBatchCount((prev) => prev + 1);
												}}
												isOffline={isOffline}
											/>
											{shouldShowIncrementalLoadingPlaceholder && (
												<Box xcss={incrementalLoadingPlaceholderStyle}>
													<CommentPlaceholder showPlaceholder />
												</Box>
											)}
										</Stack>
										<Box
											xcss={[
												addCommentContainerStyles,
												!!replyToCommentId && commentLocation === LocationValue.FOOTER
													? addCommentContainerAnimationHiddenStyles
													: addCommentContainerAnimationShowStyles,
											]}
										>
											{!shouldShowGlobalPlaceholder &&
												!shouldShowIncrementalLoadingPlaceholder &&
												shouldShowAddComment &&
												!isArchived &&
												!expanded && (
													<ObjectAddCommentContainer
														showReactions={
															!hasComments && (isEmbeddedConfluence ? hasReactions : true)
														}
														showLabels={showLabels}
														isOffline={isOffline}
														optimisticReactions
													/>
												)}
										</Box>
									</ReactionsProvider>
								)}
							</>
						)}
					</>
					{/* Editor when expanded - fixed for stickyDraft, sticky for others */}
					{expanded && (
						<Box
							xcss={[
								fg('confluence_comment_sticky_footer_enabled') && footerStickyWrapperStyle,
								shouldRenderFixedStyle ? fixedEditorStyles : stickyEditorStyles,
								pageMode === PageMode.VIEW && viewPageFooterStyle,
								pageMode !== PageMode.VIEW && footerEditorStyle,
							]}
							testId="footerCommentEditor"
							style={
								pageMode === PageMode.VIEW
									? hideWhiteSpace({
											shouldRenderFixedStyle,
											pageContentDimensions,
											fallbackWidth: width,
										})
									: undefined
							}
						>
							<Box
								xcss={baseEditorStyles}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
								style={{
									width,
									maxWidth: computedWidths.maxWidth,
								}}
							>
								<ObjectAddCommentProfileWrapper>
									<ObjectAddCommentEditor isOffline={isOffline} />
								</ObjectAddCommentProfileWrapper>
							</Box>
						</Box>
					)}
				</Box>
			</>
		);
	},
);

// This FG lets us quick revert without causing a HOT
// Don't update the legacy component, it will be removed shortly
export const ObjectCommentContainer = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(ObjectCommentContainerComponent);
